import { useCallback } from "react";
import axios, { AxiosError, AxiosResponse } from "axios";
import Config from "../config";
import { ErrorType } from "../scenes/Error/errorType";
import { ErrorResponse } from "./errorReponse";
import { GetOIDCToken } from "src/actions/OIDC";
import { Provider } from "../types";
import { captureException } from "@sentry/react";

export type AssociateRequest = {
  glbid: string;
  userId: string;
  providerId: string;
  state: string;
  serviceId: number;
};

export type AssociationRequestV2 = {
  userId: string;
  providerId: string;
};

export type BackendAssociationResponse = {
  globoId: string;
  userId: string;
  providerId: string;
  email: string;
};

export type BackendAssociationResponseV2 = {
  globoId: string;
  providerId: string;
  code: string;
};

export type BackendUpdateCPFResponse = {
  errorId?: string;
};

export type User = {
  globoId: string;
  name: string;
  photo: string;
  email: string;
};

export type UserError = {
  code?: string;
};

export type BackendAssociationResponseError = {
  code: string | null;
  email: string;
  errorId: string;
};

export type BackendTermsResponse = {
  mustAcceptTerms: boolean;
};

export type CPF = {
  document: string;
};

type ResponseError = {
  code?: number;
  errorId?: string;
  email?: string;
  providerName?: string;
  providerLogo?: string;
};

export type BackendUserFamilyTypeResponse = {
  type: string;
  errorId: string;
};

type ChannelData = {
  serviceId: string;
  name: string;
};

type AssociationData = {
  providerID: string;
  providerName: string;
  associationDate: string;
  resourceList: string[];
  channelList: ChannelData[];
};

type ProviderResponse = {
  id: string;
  name: string;
  logo: string;
  logo_white: string;
  url: string;
  is_default_authentication: boolean;
  client_id_for_alf: boolean;
  is_new_integration: boolean;
};

type ProvidersResponse = {
  providers: Array<ProviderResponse>;
};

export const useAssociate = (associateRequestData: AssociateRequest) => {
  const associate = async (): Promise<BackendAssociationResponse> => {
    let ass: BackendAssociationResponse = {
      globoId: "",
      userId: "",
      providerId: "",
      email: ""
    };
    await axios
      .post(`${Config.BE_BASE_URL}/v1/associate`, associateRequestData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${GetOIDCToken()}`
        }
      })
      .then((response: AxiosResponse<BackendAssociationResponse>) => {
        ass = response.data;
      })
      .catch((error: AxiosError<ResponseError>) => {
        captureException(new Error(`Associate response error: ${error}`));
        let errorResponse = new ErrorResponse(
          "Error to associate from API",
          error.response?.data.code ?? ErrorType.Unexpected,
          error.response?.data.errorId || ""
        );
        errorResponse.email = error.response?.data.email;
        errorResponse.providerName = error.response?.data.providerName;
        errorResponse.providerLogo = error.response?.data.providerLogo;

        throw errorResponse;
      });
    return ass;
  };

  return { associate };
};

export const userAssociateV2 = () => {
  const associate = async (
    associateRequestData: AssociationRequestV2
  ): Promise<BackendAssociationResponseV2> => {
    let ass: BackendAssociationResponseV2 = {
      globoId: "",
      providerId: "",
      code: ""
    };
    await axios
      .post(`${Config.BE_BASE_URL}/v2/associations`, associateRequestData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${GetOIDCToken()}`
        }
      })
      .then((response: AxiosResponse<BackendAssociationResponseV2>) => {
        ass = response.data;
      })
      .catch((error: AxiosError<ResponseError>) => {
        captureException(new Error(`Associations V2 response error: ${error}`));
        let errorResponse = new ErrorResponse(
          "Error to associate from API",
          error.response?.data.code ?? ErrorType.Unexpected,
          error.response?.data.errorId || ""
        );
        errorResponse.email = error.response?.data.email;
        errorResponse.providerName = error.response?.data.providerName;
        errorResponse.providerLogo = error.response?.data.providerLogo;

        throw errorResponse;
      });
    return ass;
  };

  return { associate };
};

export const useCheckTermsOfUse = (serviceId: string) => {
  const checkTerms = useCallback(async (): Promise<BackendTermsResponse> => {
    let terms: BackendTermsResponse = { mustAcceptTerms: false };
    await axios
      .get(`${Config.BE_BASE_URL}/v1/check-user-terms/` + serviceId, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${GetOIDCToken()}`
        }
      })
      .then((response: AxiosResponse<BackendTermsResponse>) => {
        terms.mustAcceptTerms = response.data.mustAcceptTerms;
      })
      .catch((error: AxiosError<ResponseError>) => {
        let errorResponse = new ErrorResponse(
          "Error to check terms from API",
          error.response?.data.code ?? ErrorType.Unexpected,
          error.response?.data.errorId || ""
        );
        errorResponse.email = error.response?.data.email;
        errorResponse.providerName = error.response?.data.providerName;
        errorResponse.providerLogo = error.response?.data.providerLogo;

        throw errorResponse;
      });
    return terms;
  }, [serviceId]);
  return { checkTerms };
};

export const useUserFamilyType = () => {
  const userType = async (): Promise<BackendUserFamilyTypeResponse> => {
    let userFamilyType: BackendUserFamilyTypeResponse = {
      type: "",
      errorId: ""
    };
    await axios
      .get(`${Config.BE_BASE_URL}/v1/user/family/type`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${GetOIDCToken()}`
        }
      })
      .then((response: AxiosResponse<BackendUserFamilyTypeResponse>) => {
        userFamilyType.type = response.data.type;
        userFamilyType.errorId = response.data.errorId;
      })
      .catch((error: AxiosError<ResponseError>) => {
        let errorResponse = new ErrorResponse(
          "Error to get a type",
          error.response?.data.code ?? ErrorType.Unexpected,
          error.response?.data.errorId || ""
        );
        errorResponse.email = error.response?.data.email;
        errorResponse.providerName = error.response?.data.providerName;
        errorResponse.providerLogo = error.response?.data.providerLogo;

        throw errorResponse;
      });
    return userFamilyType;
  };
  return { userType };
};

export const useAssociationValidate = (
  associateRequestData: AssociateRequest
) => {
  const { glbid, userId, providerId, state, serviceId } = associateRequestData;

  const associationValidate = useCallback(async (): Promise<boolean> => {
    let isValid = false;
    await axios
      .post(
        `${Config.BE_BASE_URL}/v1/associate/validate`,
        { glbid, userId, providerId, state, serviceId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${GetOIDCToken()}`
          }
        }
      )
      .then(() => {
        isValid = true;
      })
      .catch((error: AxiosError<ResponseError>) => {
        captureException(
          new Error(`Associate validate response error: ${error}`)
        );
        let errorResponse = new ErrorResponse(
          "Error to validate association",
          error.response?.data.code ?? ErrorType.Unexpected,
          error.response?.data.errorId || ""
        );
        errorResponse.email = error.response?.data.email;
        errorResponse.providerName = error.response?.data.providerName;
        errorResponse.providerLogo = error.response?.data.providerLogo;

        throw errorResponse;
      });
    return isValid;
  }, [glbid, userId, providerId, state, serviceId]);
  return { associationValidate };
};

export const updateRegister = async (
  payload: CPF,
  retries = 2
): Promise<void> => {
  await axios
    .patch<BackendUpdateCPFResponse>(`${Config.BE_BASE_URL}/v1/user`, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${GetOIDCToken()}`
      }
    })
    .then(() => {
      return;
    })
    .catch((error: AxiosError<ResponseError>) => {
      if (retries > 0) {
        return updateRegister(payload, retries - 1);
      }

      if (error.response) {
        if (error.response.status === 404) {
          throw new ErrorResponse(
            "Error to update cpf from API from Not Found",
            error.response.status ?? ErrorType.Unexpected,
            error.response.data.errorId || ""
          );
        }

        if (error.response.status === 400) {
          throw new ErrorResponse(
            "Error to update cpf from API from Bad Request",
            error.response.status ?? ErrorType.Unexpected,
            error.response.data.errorId || ""
          );
        }

        if (error.response.status === 500) {
          throw new ErrorResponse(
            "Error to update cpf from API from Internal Server Error",
            error.response.status ?? ErrorType.Unexpected,
            error.response.data.errorId || ""
          );
        }

        throw error;
      }
    });
};

export const getAssociations = async (
  globoId: string,
  retries = 2
): Promise<AssociationData[]> => {
  return await axios
    .get(`${Config.BE_BASE_URL}/v1/associations/${globoId}`, {
      headers: {
        "Content-Type": "application/json"
      }
    })
    .then((response: AxiosResponse<AssociationData[]>) => {
      return response.data;
    })
    .catch((error: AxiosError<ResponseError>) => {
      captureException(new Error(`Associations response error: ${error}`));
      if (error.response?.status === 404) {
        return [];
      }

      if (retries > 0) {
        return getAssociations(globoId, retries - 1);
      }

      throw new ErrorResponse(
        "Error to get associations",
        ErrorType.Unexpected,
        error.response?.data?.errorId || ""
      );
    });
};

const HIDDEN_PROVIDERS = ["adb3c4c8-c561-4dbf-a18a-bb1a69d10597"];

export const getProvidersByService = async (
  serviceId: string
): Promise<Provider[]> => {
  const response = await axios.get(
    `${Config.BE_BASE_URL}/v1/providers/list/${serviceId}`,
    {
      headers: {
        Authorization: `Bearer ${GetOIDCToken()}`
      }
    }
  );
  return response.data.providers.map(
    (provider: Provider): Provider => ({
      id: provider.id,
      name: provider.name,
      logo: provider.logo,
      logo_white: provider.logo_white,
      url: provider.url,
      is_default_authentication: provider.is_default_authentication,
      client_id_for_alf: provider.client_id_for_alf,
      is_new_integration: provider.is_new_integration,
      is_hidden: HIDDEN_PROVIDERS.includes(provider.id)
    })
  );
};

export const evaluateProviderMigration = async (
  provider: string
): Promise<boolean> => {
  const response = await axios.get(
    `${Config.BE_BASE_URL}/v1/evaluate/migrate-${provider}`,
    {
      headers: {
        Authorization: `Bearer ${GetOIDCToken()}`
      }
    }
  );
  return response.data.result;
};
